import { getEnergyTariffByType } from '@features/Energy/utils';
import { EnergyQuoteFragment, EnergyQuoteType } from '@generated/gql/graphql';
import { BundleBasketData } from '@src/types/bundle';

import { formatCurrency } from './format';

enum BotMessagingVariant {
  NoSelection = 'no_selection',
  Energy = 'energy',
  Broadband = 'broadband',
  Insurance = 'insurance',
  Mobile = 'mobile',
  Energy_Broadband = 'energy_broadband',
  Energy_Insurance = 'energy_insurance',
  Energy_Mobile = 'energy_mobile',
  Broadband_Insurance = 'broadband_insurance',
  Broadband_Mobile = 'broadband_mobile',
  Insurance_Mobile = 'insurance_mobile',
  Mobile_Mobile2 = 'mobile_mobile2',
  Energy_Broadband_Insurance = 'energy_broadband_insurance',
  Energy_Broadband_Mobile = 'energy_broadband_mobile',
  Energy_Insurance_Mobile = 'energy_insurance_mobile',
  Energy_Mobile_Mobile2 = 'energy_mobile_mobile2',
  Broadband_Insurance_Mobile = 'broadband_insurance_mobile',
  Broadband_Mobile_Mobile2 = 'broadband_mobile_mobile2',
  Insurance_Mobile_Mobile2 = 'insurance_mobile_mobile2',
  Energy_Broadband_Insurance_Mobile = 'energy_broadband_insurance_mobile',
  Energy_Broadband_Mobile_Mobile2 = 'energy_broadband_mobile_mobile2',
  Energy_Insurance_Mobile_Mobile2 = 'energy_insurance_mobile_mobile2',
  Broadband_Insurance_Mobile_Mobile2 = 'broadband_insurance_mobile_mobile2',
  Energy_Broadband_Insurance_Mobile_Mobile2 = 'energy_broadband_insurance_mobile_mobile2',
}

const getMessagingVariant = (
  energySelected: boolean,
  broadbandSelected: boolean,
  mobileSelected: boolean,
  mobile2Selected: boolean,
  insuranceSelected: boolean
): BotMessagingVariant => {
  if (
    energySelected &&
    broadbandSelected &&
    mobileSelected &&
    mobile2Selected &&
    insuranceSelected
  ) {
    return BotMessagingVariant.Energy_Broadband_Insurance_Mobile_Mobile2;
  }

  if (
    energySelected &&
    broadbandSelected &&
    mobileSelected &&
    insuranceSelected
  ) {
    return BotMessagingVariant.Energy_Broadband_Insurance_Mobile;
  }
  if (
    energySelected &&
    mobileSelected &&
    mobile2Selected &&
    insuranceSelected
  ) {
    return BotMessagingVariant.Energy_Insurance_Mobile_Mobile2;
  }
  if (
    broadbandSelected &&
    mobileSelected &&
    mobile2Selected &&
    insuranceSelected
  ) {
    return BotMessagingVariant.Broadband_Insurance_Mobile_Mobile2;
  }
  if (energySelected && broadbandSelected && insuranceSelected) {
    return BotMessagingVariant.Energy_Broadband_Insurance;
  }
  if (energySelected && mobileSelected && insuranceSelected) {
    return BotMessagingVariant.Energy_Insurance_Mobile;
  }
  if (broadbandSelected && mobileSelected && insuranceSelected) {
    return BotMessagingVariant.Broadband_Insurance_Mobile;
  }
  if (mobileSelected && mobile2Selected && insuranceSelected) {
    return BotMessagingVariant.Insurance_Mobile_Mobile2;
  }
  if (energySelected && insuranceSelected) {
    return BotMessagingVariant.Energy_Insurance;
  }
  if (broadbandSelected && insuranceSelected) {
    return BotMessagingVariant.Broadband_Insurance;
  }
  if (mobileSelected && insuranceSelected) {
    return BotMessagingVariant.Insurance_Mobile;
  }

  if (insuranceSelected) {
    return BotMessagingVariant.Insurance;
  }

  if (
    energySelected &&
    broadbandSelected &&
    mobileSelected &&
    mobile2Selected
  ) {
    return BotMessagingVariant.Energy_Broadband_Mobile_Mobile2;
  }
  if (energySelected && broadbandSelected && mobileSelected) {
    return BotMessagingVariant.Energy_Broadband_Mobile;
  }
  if (energySelected && mobileSelected && mobile2Selected) {
    return BotMessagingVariant.Energy_Mobile_Mobile2;
  }
  if (broadbandSelected && mobileSelected && mobile2Selected) {
    return BotMessagingVariant.Broadband_Mobile_Mobile2;
  }
  if (energySelected && broadbandSelected) {
    return BotMessagingVariant.Energy_Broadband;
  }
  if (energySelected && mobileSelected) {
    return BotMessagingVariant.Energy_Mobile;
  }
  if (broadbandSelected && mobileSelected) {
    return BotMessagingVariant.Broadband_Mobile;
  }
  if (mobileSelected && mobile2Selected) {
    return BotMessagingVariant.Mobile_Mobile2;
  }
  if (energySelected) {
    return BotMessagingVariant.Energy;
  }
  if (broadbandSelected) {
    return BotMessagingVariant.Broadband;
  }
  if (mobileSelected) {
    return BotMessagingVariant.Mobile;
  }

  return BotMessagingVariant.NoSelection;
};

type ScenarioKey = 'default' | 'tbyb' | 'insurance' | 'insurance_tbyb';
type MessageFn = (args: {
  energyPrice: string | null;
  nextWelcomeBonus: string;
}) => string | null;

const priceSegment = (formattedPrice: string | null) =>
  formattedPrice ? ` to <b>${formattedPrice}</b> a month` : '';

const noMessage: MessageFn = () => null;

type SelectedServiceFlags = {
  energySelected: boolean;
  broadbandSelected: boolean;
  mobileSelected: boolean;
  mobile2Selected: boolean;
  insuranceSelected: boolean;
};

const NEXT_WELCOME_BONUS_BY_SERVICE_TYPE_COUNT: Record<number, number> = {
  1: 50,
  2: 150,
  3: 250,
  4: 0,
};

const getServiceTypeCount = ({
  energySelected,
  broadbandSelected,
  mobileSelected,
  insuranceSelected,
}: SelectedServiceFlags) =>
  [energySelected, broadbandSelected, mobileSelected, insuranceSelected].filter(
    Boolean
  ).length;

const getNextWelcomeBonus = (selected: SelectedServiceFlags) =>
  NEXT_WELCOME_BONUS_BY_SERVICE_TYPE_COUNT[getServiceTypeCount(selected)] ?? 0;

// Messages spreadsheet
// https://docs.google.com/spreadsheets/d/1eaAntqwFkykVw9kGwRSEv3Yo9KAqglYdY5qb4k3NUTE/edit?gid=0#gid=0
const messages: Record<BotMessagingVariant, Record<ScenarioKey, MessageFn>> = {
  [BotMessagingVariant.NoSelection]: {
    default: () =>
      "Add a service to your bundle to get started. The more you bundle, the more you'll save on your energy.",
    tbyb: () =>
      "Add a service to your bundle to get started. The more you bundle, the more you'll save on your energy.",
    insurance: () =>
      "Add a service to your bundle to get started. The more you bundle, the more you'll save on your energy.",
    insurance_tbyb: () =>
      "Add a service to your bundle to get started. The more you bundle, the more you'll save on your energy.",
  },
  [BotMessagingVariant.Energy]: {
    default: ({ energyPrice: ep, nextWelcomeBonus }) =>
      `Add another service to reduce your Energy price${priceSegment(ep)} and get a ${nextWelcomeBonus} welcome bonus on your Cashback Card.`,
    tbyb: ({ energyPrice: ep, nextWelcomeBonus }) =>
      `You've unlocked 6 months free Broadband! Add another service to reduce your Energy price${priceSegment(ep)} and get a ${nextWelcomeBonus} welcome bonus on your Cashback Card.`,
    insurance: ({ energyPrice: ep, nextWelcomeBonus }) =>
      `Add another service to reduce your Energy price${priceSegment(ep)} and get a ${nextWelcomeBonus} welcome bonus on your Cashback Card.`,
    insurance_tbyb: ({ energyPrice: ep, nextWelcomeBonus }) =>
      `You've unlocked 6 months free Broadband! Add another service to reduce your Energy price${priceSegment(ep)} and get a ${nextWelcomeBonus} welcome bonus on your Cashback Card.`,
  },
  [BotMessagingVariant.Broadband]: {
    default: ({ nextWelcomeBonus }) =>
      `You've unlocked cheaper Energy tariffs! Add another service to get a ${nextWelcomeBonus} welcome bonus on your Cashback Card.`,
    tbyb: ({ nextWelcomeBonus }) =>
      `Add another service to unlock 6 months free Broadband and get a ${nextWelcomeBonus} welcome bonus on your Cashback Card!`,
    insurance: ({ nextWelcomeBonus }) =>
      `You've unlocked cheaper Energy tariffs! Add another service to get a ${nextWelcomeBonus} welcome bonus on your Cashback Card.`,
    insurance_tbyb: ({ nextWelcomeBonus }) =>
      `Add another service to unlock 6 months free Broadband and get a ${nextWelcomeBonus} welcome bonus on your Cashback Card!`,
  },
  [BotMessagingVariant.Insurance]: {
    default: noMessage,
    tbyb: noMessage,
    insurance: ({ nextWelcomeBonus }) =>
      `You've unlocked cheaper Energy tariffs! Add another service to get a ${nextWelcomeBonus} welcome bonus on your Cashback Card.`,
    insurance_tbyb: ({ nextWelcomeBonus }) =>
      `You've unlocked cheaper Energy tariffs and 6 months free Broadband! Adding another service will also get you a ${nextWelcomeBonus} welcome bonus on your Cashback Card.`,
  },
  [BotMessagingVariant.Mobile]: {
    default: ({ nextWelcomeBonus }) =>
      `You've unlocked cheaper Energy tariffs! Add another service to get a ${nextWelcomeBonus} welcome bonus on your Cashback Card.`,
    tbyb: ({ nextWelcomeBonus }) =>
      `You've unlocked 6 months free Broadband and cheaper Energy tariffs! Adding another service will also get you a ${nextWelcomeBonus} welcome bonus on your Cashback Card.`,
    insurance: ({ nextWelcomeBonus }) =>
      `You've unlocked cheaper Energy tariffs! Add another service to get a ${nextWelcomeBonus} welcome bonus on your Cashback Card.`,
    insurance_tbyb: ({ nextWelcomeBonus }) =>
      `You've unlocked 6 months free Broadband and cheaper Energy tariffs! Adding another service will also get you a ${nextWelcomeBonus} welcome bonus on your Cashback Card.`,
  },
  [BotMessagingVariant.Mobile_Mobile2]: {
    default: ({ nextWelcomeBonus }) =>
      `You've unlocked our cheapest Energy tariffs! Add another service to unlock £400 towards exit fees from your current suppliers and a ${nextWelcomeBonus} welcome bonus on your Cashback Card!`,
    tbyb: ({ nextWelcomeBonus }) =>
      `You've unlocked 6 months free Broadband and our cheapest Energy tariffs! Add another service to unlock £400 towards exit fees from your current suppliers and a ${nextWelcomeBonus} welcome bonus on your Cashback Card!`,
    insurance: ({ nextWelcomeBonus }) =>
      `You've unlocked our cheapest Energy tariffs! Add another service to unlock £400 towards exit fees from your current suppliers and a ${nextWelcomeBonus} welcome bonus on your Cashback Card!`,
    insurance_tbyb: ({ nextWelcomeBonus }) =>
      `You've unlocked 6 months free Broadband and our cheapest Energy tariffs! Add another service to get a ${nextWelcomeBonus} welcome bonus on your Cashback Card and £400 towards exit fees from your current suppliers!`,
  },
  [BotMessagingVariant.Energy_Broadband]: {
    default: ({ energyPrice: ep, nextWelcomeBonus }) =>
      `Reduce your Energy price${priceSegment(ep)} when you add a Mobile SIM. This will also get you a ${nextWelcomeBonus} welcome bonus on your Cashback Card, as well as £400 towards exit fees from your current suppliers.`,
    tbyb: ({ energyPrice: ep, nextWelcomeBonus }) =>
      `Reduce your Energy price${priceSegment(ep)} when you add a Mobile SIM. This will also get you a ${nextWelcomeBonus} welcome bonus on your Cashback Card, as well as £400 towards exit fees from your current suppliers.`,
    insurance: ({ energyPrice: ep, nextWelcomeBonus }) =>
      `Add another service to reduce your Energy price${priceSegment(ep)} and get a ${nextWelcomeBonus} welcome bonus on your Cashback Card, as well as £400 towards exit fees from your current suppliers.`,
    insurance_tbyb: ({ energyPrice: ep, nextWelcomeBonus }) =>
      `Add another service to reduce your Energy price${priceSegment(ep)} and get a ${nextWelcomeBonus} welcome bonus on your Cashback Card, as well as £400 towards exit fees from your current suppliers.`,
  },
  [BotMessagingVariant.Energy_Insurance]: {
    default: noMessage,
    tbyb: noMessage,
    insurance: ({ energyPrice: ep, nextWelcomeBonus }) =>
      `Add another service to reduce your Energy price${priceSegment(ep)} and get a ${nextWelcomeBonus} welcome bonus on your Cashback Card, as well as £400 towards exit fees from your current suppliers.`,
    insurance_tbyb: ({ energyPrice: ep, nextWelcomeBonus }) =>
      `Add another service to reduce your Energy price${priceSegment(ep)} and get a ${nextWelcomeBonus} welcome bonus on your Cashback Card, as well as £400 towards exit fees from your current suppliers.`,
  },
  [BotMessagingVariant.Energy_Mobile]: {
    default: ({ energyPrice: ep, nextWelcomeBonus }) =>
      `Add Broadband or another Mobile SIM to reduce your Energy price${priceSegment(ep)} and unlock £400 towards exit fees from your current suppliers. Adding Broadband will also increase the welcome bonus on your Cashback Card to ${nextWelcomeBonus}.`,
    tbyb: ({ energyPrice: ep, nextWelcomeBonus }) =>
      `Add Broadband or another Mobile SIM to reduce your Energy price${priceSegment(ep)} and unlock £400 towards exit fees from your current suppliers. Adding Broadband will also increase the welcome bonus on your Cashback Card to ${nextWelcomeBonus}.`,
    insurance: ({ energyPrice: ep, nextWelcomeBonus }) =>
      `Add Broadband, Insurance, or another Mobile SIM to reduce your Energy price${priceSegment(ep)} and unlock £400 towards exit fees from your current suppliers. Adding Broadband or Insurance will also increase the welcome bonus on your Cashback Card to ${nextWelcomeBonus}.`,
    insurance_tbyb: ({ energyPrice: ep, nextWelcomeBonus }) =>
      `Add Broadband, Insurance, or another Mobile SIM to reduce your Energy price${priceSegment(ep)} and unlock £400 towards exit fees from your current suppliers. Adding Broadband or Insurance will also increase the welcome bonus on your Cashback Card to ${nextWelcomeBonus}.`,
  },
  [BotMessagingVariant.Broadband_Insurance]: {
    default: noMessage,
    tbyb: noMessage,
    insurance: ({ nextWelcomeBonus }) =>
      `You've unlocked our cheapest Energy tariffs! Add another service to get a ${nextWelcomeBonus} welcome bonus on your Cashback Card, as well as £400 towards exit fees from your current suppliers.`,
    insurance_tbyb: ({ nextWelcomeBonus }) =>
      `You've unlocked our cheapest Energy tariffs! Add another service to get a ${nextWelcomeBonus} welcome bonus on your Cashback Card, as well as £400 towards exit fees from your current suppliers.`,
  },
  [BotMessagingVariant.Broadband_Mobile]: {
    default: ({ nextWelcomeBonus }) =>
      `You've unlocked our cheapest Energy tariffs! Add Energy or another Mobile SIM to get £400 towards exit fees from your current suppliers. Adding Energy will also increase the welcome bonus on your Cashback Card to ${nextWelcomeBonus}.`,
    tbyb: ({ nextWelcomeBonus }) =>
      `You've unlocked our cheapest Energy tariffs! Add Energy or another Mobile SIM to get £400 towards exit fees from your current suppliers. Adding Energy will also increase the welcome bonus on your Cashback Card to ${nextWelcomeBonus}.`,
    insurance: ({ nextWelcomeBonus }) =>
      `You've unlocked our cheapest Energy tariffs! Add Energy, Insurance, or another Mobile SIM to get £400 towards exit fees from your current suppliers. Adding Energy or Insurance will also increase the welcome bonus on your Cashback Card to ${nextWelcomeBonus}.`,
    insurance_tbyb: ({ nextWelcomeBonus }) =>
      `You've unlocked our cheapest Energy tariffs! Add Energy, Insurance, or another Mobile SIM to get £400 towards exit fees from your current suppliers. Adding Energy or Insurance will also increase the welcome bonus on your Cashback Card to ${nextWelcomeBonus}.`,
  },
  [BotMessagingVariant.Insurance_Mobile]: {
    default: noMessage,
    tbyb: noMessage,
    insurance: ({ nextWelcomeBonus }) =>
      `You've unlocked our cheapest Energy tariffs. Add Energy, Broadband, or another Mobile SIM to get £400 towards exit fees from your current suppliers. Adding Energy or Broadband will also increase the welcome bonus on your Cashback Card to ${nextWelcomeBonus}!`,
    insurance_tbyb: ({ nextWelcomeBonus }) =>
      `You've unlocked our cheapest Energy tariffs. Add Energy, Broadband, or another Mobile SIM to get £400 towards exit fees from your current suppliers. Adding Energy or Broadband will also increase the welcome bonus on your Cashback Card to ${nextWelcomeBonus}!`,
  },
  [BotMessagingVariant.Energy_Mobile_Mobile2]: {
    default: ({ nextWelcomeBonus }) =>
      `Don't forget to check out our Broadband deals! Adding Broadband will also increase the welcome bonus on your Cashback Card to ${nextWelcomeBonus}!`,
    tbyb: ({ nextWelcomeBonus }) =>
      `Don't forget to check out our 6 months free Broadband offer! Adding Broadband will also increase the welcome bonus on your Cashback Card to ${nextWelcomeBonus}!`,
    insurance: ({ nextWelcomeBonus }) =>
      `Don't forget to check out our Broadband and our Insurance. Adding another service will increase the welcome bonus on your Cashback Card to ${nextWelcomeBonus}!`,
    insurance_tbyb: ({ nextWelcomeBonus }) =>
      `Don't forget to check out our 6 months free Broadband offer and our Insurance! Adding another service will increase the welcome bonus on your Cashback Card to ${nextWelcomeBonus}!`,
  },
  [BotMessagingVariant.Broadband_Mobile_Mobile2]: {
    default: ({ nextWelcomeBonus }) =>
      `You've unlocked our cheapest Energy tariffs. Adding another service will increase the welcome bonus on your Cashback Card to ${nextWelcomeBonus}!`,
    tbyb: ({ nextWelcomeBonus }) =>
      `You've unlocked our cheapest Energy tariffs. Adding another service will increase the welcome bonus on your Cashback Card to ${nextWelcomeBonus}!`,
    insurance: ({ nextWelcomeBonus }) =>
      `You've unlocked our cheapest Energy tariffs. Adding another service will increase the welcome bonus on your Cashback Card to ${nextWelcomeBonus}!`,
    insurance_tbyb: ({ nextWelcomeBonus }) =>
      `You've unlocked our cheapest Energy tariffs. Adding another service will increase the welcome bonus on your Cashback Card to ${nextWelcomeBonus}!`,
  },
  [BotMessagingVariant.Insurance_Mobile_Mobile2]: {
    default: noMessage,
    tbyb: noMessage,
    insurance: ({ nextWelcomeBonus }) =>
      `You've unlocked our cheapest Energy tariffs. Adding another service will increase the welcome bonus on your Cashback Card to ${nextWelcomeBonus}!`,
    insurance_tbyb: ({ nextWelcomeBonus }) =>
      `You've unlocked our cheapest Energy tariffs. Adding another service will increase the welcome bonus on your Cashback Card to ${nextWelcomeBonus}!`,
  },
  [BotMessagingVariant.Energy_Broadband_Insurance]: {
    default: noMessage,
    tbyb: noMessage,
    insurance: ({ nextWelcomeBonus }) =>
      `Don't forget to check out our Mobile deals. Adding another service will increase the welcome bonus on your Cashback Card to ${nextWelcomeBonus}!`,
    insurance_tbyb: ({ nextWelcomeBonus }) =>
      `Don't forget to check out our Mobile deals. Adding another service will increase the welcome bonus on your Cashback Card to ${nextWelcomeBonus}!`,
  },
  [BotMessagingVariant.Energy_Broadband_Mobile]: {
    default: () => "Don't forget to check out our multi-SIM offer!",
    tbyb: () => "Don't forget to check out our multi-SIM offer!",
    insurance: ({ nextWelcomeBonus }) =>
      `Don't forget to check out our Insurance. Adding it will increase the welcome bonus on your Cashback Card to ${nextWelcomeBonus}!`,
    insurance_tbyb: ({ nextWelcomeBonus }) =>
      `Don't forget to check out our Insurance. Adding it will increase the welcome bonus on your Cashback Card to ${nextWelcomeBonus}!`,
  },
  [BotMessagingVariant.Energy_Insurance_Mobile]: {
    default: noMessage,
    tbyb: noMessage,
    insurance: ({ nextWelcomeBonus }) =>
      `Don't forget to check out our Broadband deals! Adding another service will increase the welcome bonus on your Cashback Card to ${nextWelcomeBonus}!`,
    insurance_tbyb: ({ nextWelcomeBonus }) =>
      `Don't forget to check out our 6 months free Broadband offer. Adding another service will increase the welcome bonus on your Cashback Card to ${nextWelcomeBonus}!`,
  },
  [BotMessagingVariant.Broadband_Insurance_Mobile]: {
    default: noMessage,
    tbyb: noMessage,
    insurance: ({ nextWelcomeBonus }) =>
      `You've unlocked our cheapest Energy tariffs. Adding another service will increase the welcome bonus on your Cashback Card to ${nextWelcomeBonus}!`,
    insurance_tbyb: ({ nextWelcomeBonus }) =>
      `You've unlocked our cheapest Energy tariffs. Adding another service will increase the welcome bonus on your Cashback Card to ${nextWelcomeBonus}!`,
  },
  [BotMessagingVariant.Energy_Broadband_Mobile_Mobile2]: {
    default: noMessage,
    tbyb: noMessage,
    insurance: () => "Don't forget to check out our Insurance!",
    insurance_tbyb: () => "Don't forget to check out our Insurance!",
  },
  [BotMessagingVariant.Energy_Insurance_Mobile_Mobile2]: {
    default: noMessage,
    tbyb: noMessage,
    insurance: () => "Don't forget to check out our Broadband deals!",
    insurance_tbyb: () =>
      "Don't forget to check out our 6 months free Broadband offer!",
  },
  [BotMessagingVariant.Broadband_Insurance_Mobile_Mobile2]: {
    default: noMessage,
    tbyb: noMessage,
    insurance: () => "You've unlocked our cheapest Energy tariffs!",
    insurance_tbyb: () => "You've unlocked our cheapest Energy tariffs!",
  },
  [BotMessagingVariant.Energy_Broadband_Insurance_Mobile]: {
    default: noMessage,
    tbyb: noMessage,
    insurance: () => "Don't forget to check out our multi-SIM offer!",
    insurance_tbyb: () => "Don't forget to check out our multi-SIM offer!",
  },
  [BotMessagingVariant.Energy_Broadband_Insurance_Mobile_Mobile2]: {
    default: noMessage,
    tbyb: noMessage,
    insurance: noMessage,
    insurance_tbyb: noMessage,
  },
};

const getNextQuotesCharges = (
  nextQuotes?: EnergyQuoteFragment[] | undefined
) => {
  if (!nextQuotes) {
    return {
      variable: null,
      fixed: null,
      tracker: null,
    };
  }

  return {
    variable:
      getEnergyTariffByType(nextQuotes, EnergyQuoteType.Variable)
        ?.monthlyCost ?? null,
    fixed:
      getEnergyTariffByType(nextQuotes, EnergyQuoteType.Fixed)?.monthlyCost ??
      null,
    tracker:
      getEnergyTariffByType(nextQuotes, EnergyQuoteType.Tracker)?.monthlyCost ??
      null,
  };
};

const formatBasket = (
  basket: BundleBasketData,
  nextEnergyTariffs: EnergyQuoteFragment[] | undefined
) => {
  const { energy, mobile, broadband, insurance } = basket;
  const simCount = mobile?.selectedSimCount ?? 0;

  const isEnergySelected = !!energy?.selectedQuote;

  return {
    energySelected: isEnergySelected,
    broadbandSelected: !!broadband.yourService,
    mobileSelected: simCount > 0,
    mobile2Selected: simCount > 1,
    insuranceSelected: insurance.selected,
    insuranceAvailable: insurance.available,
    quoteCharges: getNextQuotesCharges(nextEnergyTariffs),
    selectedEnergyTariff:
      energy?.selectedQuote?.electricity?.quoteType ||
      energy?.selectedQuote?.gas?.quoteType,
    tbybEligible: broadband.tbybEligible,
  };
};

const bundleBotTextCheck = (
  basket: BundleBasketData,
  nextTariffs: EnergyQuoteFragment[] | undefined
): Nullable<string[]> => {
  const {
    energySelected,
    broadbandSelected,
    mobileSelected,
    mobile2Selected,
    insuranceSelected,
    insuranceAvailable,
    quoteCharges,
    selectedEnergyTariff,
    tbybEligible,
  } = formatBasket(basket, nextTariffs);

  const messageVariant = getMessagingVariant(
    energySelected,
    broadbandSelected,
    mobileSelected,
    mobile2Selected,
    insuranceSelected
  );

  const chargeByTariff = {
    [EnergyQuoteType.Fixed]: quoteCharges?.fixed ?? null,
    [EnergyQuoteType.Tracker]: quoteCharges?.tracker ?? null,
    [EnergyQuoteType.Variable]: quoteCharges?.variable ?? null,
  };
  const energyPriceCharge = selectedEnergyTariff
    ? chargeByTariff[selectedEnergyTariff]
    : null;

  const formattedEnergyPrice = energyPriceCharge
    ? formatCurrency(energyPriceCharge)
    : null;

  const scenarioKey: ScenarioKey =
    insuranceAvailable && tbybEligible
      ? 'insurance_tbyb'
      : insuranceAvailable
        ? 'insurance'
        : tbybEligible
          ? 'tbyb'
          : 'default';

  const nextWelcomeBonus = getNextWelcomeBonus({
    energySelected,
    broadbandSelected,
    mobileSelected,
    mobile2Selected,
    insuranceSelected,
  });

  const messageFn = messages[messageVariant][scenarioKey];

  const message = messageFn({
    energyPrice: formattedEnergyPrice,
    nextWelcomeBonus: `£${nextWelcomeBonus}`,
  });

  if (!message) {
    return null;
  }

  return ['<h4> Top tip! </h4>', message];
};

export { bundleBotTextCheck };
